import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { type FormEvent, type ReactNode, useId } from 'react';

import { cssVar } from '../../theme/tokens';
import { IconButton } from './IconButton';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';
/** MUI widths 444, 600 and 900 px; `xl` is the 1040 px panel (dialogs with a live preview). */
const MAX_WIDTH = { sm: 'xs', md: 'sm', lg: 'md', xl: false } as const;

export interface DialogProps {
  open: boolean;
  /** Closes on Escape, the backdrop and ×; omit while an action is pending to block closing. */
  onClose?: () => void;
  title: ReactNode;
  /** Short text under the title, announced as the dialog's description. */
  description?: ReactNode;
  /** Buttons at the bottom (right-aligned unless `actionsAlign` says otherwise). */
  actions?: ReactNode;
  actionsAlign?: 'end' | 'center';
  size?: DialogSize;
  /** Show the × button (default true). */
  closeButton?: boolean;
  /** Colour of × (the legacy forms use a red ×). */
  closeVariant?: 'ghost' | 'danger';
  /** Disables × (for example while saving). */
  closeDisabled?: boolean;
  /** Wraps body and actions in a form with this submit handler (submit buttons then work). */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Dividers above and below a scrolling body. */
  dividers?: boolean;
  /** Fill the screen on phones (below the `sm` breakpoint), for dialogs with tables or steps. */
  fullScreenOnMobile?: boolean;
  /** An icon tile before the title (confirmations): the icon and its tone. */
  icon?: { node: ReactNode; tone?: 'neutral' | 'danger' | 'primary' };
  /** A full-width band between the title and the body, for example the steps of a wizard. */
  banner?: ReactNode;
  children?: ReactNode;
}

const ICON_TONE = {
  neutral: { bg: cssVar('tile'), fg: cssVar('text') },
  danger: { bg: cssVar('danger-subtle'), fg: cssVar('danger-text') },
  primary: { bg: cssVar('primary-subtle'), fg: cssVar('primary-text') },
} as const;

/** A modal dialog: title, optional description, body and actions; focus is trapped inside. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  actions,
  actionsAlign = 'end',
  size = 'md',
  closeButton = true,
  closeVariant = 'ghost',
  closeDisabled = false,
  onSubmit,
  dividers = false,
  fullScreenOnMobile = false,
  icon,
  banner,
  children,
}: DialogProps) {
  const titleId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const descriptionId = useId();

  const inner = (
    <>
      {icon ? (
        <Box
          aria-hidden
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: cssVar('space-12'),
            height: cssVar('space-12'),
            mx: 6,
            mt: 6,
            borderRadius: cssVar('radius-lg'),
            bgcolor: ICON_TONE[icon.tone ?? 'neutral'].bg,
            color: ICON_TONE[icon.tone ?? 'neutral'].fg,
            '& .MuiSvgIcon-root': { fontSize: cssVar('space-6') },
          }}
        >
          {icon.node}
        </Box>
      ) : null}
      <DialogTitle
        id={titleId}
        variant="h4"
        sx={{ pr: closeButton ? 16 : undefined, pt: icon ? 4 : undefined }}
      >
        {title}
      </DialogTitle>
      {closeButton && onClose ? (
        <IconButton
          label="Close"
          tooltip={false}
          variant={closeVariant}
          size="sm"
          onClick={onClose}
          disabled={closeDisabled}
          // v3: the close button sits on a tile.
          sx={{ position: 'absolute', right: 4, top: 4, bgcolor: cssVar('tile') }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
      {banner ? (
        <Box
          sx={{
            flex: 'none',
            borderTop: `1px solid ${cssVar('border')}`,
            bgcolor: cssVar('surface-sunken'),
          }}
        >
          {banner}
        </Box>
      ) : null}
      {description || children ? (
        <DialogContent dividers={dividers}>
          {description ? (
            <DialogContentText id={descriptionId} component="div" sx={{ mb: children ? 4 : 0 }}>
              {description}
            </DialogContentText>
          ) : null}
          {children}
        </DialogContent>
      ) : null}
      {actions ? (
        <DialogActions
          sx={{
            px: 6,
            py: 4,
            gap: 2,
            justifyContent: actionsAlign === 'center' ? 'center' : 'flex-end',
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      maxWidth={MAX_WIDTH[size]}
      fullWidth
      fullScreen={fullScreenOnMobile && phone}
      slotProps={size === 'xl' ? { paper: { sx: { maxWidth: cssVar('panel-xl') } } } : undefined}
    >
      {onSubmit ? (
        <Box
          component="form"
          noValidate
          onSubmit={onSubmit}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto' }}
        >
          {inner}
        </Box>
      ) : (
        inner
      )}
    </MuiDialog>
  );
}
